import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const BPRituals = () => {
  const { t } = useTranslation();
  const [activeRitual, setActiveRitual] = useState(0);

  const rituals = [
    {
      id: 1,
      icon: 'ri-sun-line',
      title: t('bp_rit1_title'),
      subtitle: t('bp_rit1_sub'),
      duration: t('bp_rit1_duration'),
      difficulty: t('bp_rit1_level'),
      products: ['Blue Protect Soap', 'Blue Protect Spray'],
      steps: [
        { num: 1, text: t('bp_rit1_s1') },
        { num: 2, text: t('bp_rit1_s2') },
        { num: 3, text: t('bp_rit1_s3') },
        { num: 4, text: t('bp_rit1_s4') },
      ],
      tip: t('bp_rit1_tip'),
      color: 'from-[#050010] to-[#0a0820]',
      border: 'border-violet-500/20',
      accent: 'text-violet-400',
      accentBg: 'bg-violet-600/15 border-violet-500/20',
    },
    {
      id: 2,
      icon: 'ri-drop-line',
      title: t('bp_rit2_title'),
      subtitle: t('bp_rit2_sub'),
      duration: t('bp_rit2_duration'),
      difficulty: t('bp_rit2_level'),
      products: ['Blue Protect Powder', 'Blue Protect Balls'],
      steps: [
        { num: 1, text: t('bp_rit2_s1') },
        { num: 2, text: t('bp_rit2_s2') },
        { num: 3, text: t('bp_rit2_s3') },
        { num: 4, text: t('bp_rit2_s4') },
        { num: 5, text: t('bp_rit2_s5') },
      ],
      tip: t('bp_rit2_tip'),
      color: 'from-[#070015] to-[#0a0820]',
      border: 'border-fuchsia-500/20',
      accent: 'text-fuchsia-400',
      accentBg: 'bg-fuchsia-600/15 border-fuchsia-500/20',
    },
    {
      id: 3,
      icon: 'ri-home-smile-line',
      title: t('bp_rit3_title'),
      subtitle: t('bp_rit3_sub'),
      duration: t('bp_rit3_duration'),
      difficulty: t('bp_rit3_level'),
      products: ['Blue Protect Powder', 'Blue Protect Spray'],
      steps: [
        { num: 1, text: t('bp_rit3_s1') },
        { num: 2, text: t('bp_rit3_s2') },
        { num: 3, text: t('bp_rit3_s3') },
        { num: 4, text: t('bp_rit3_s4') },
      ],
      tip: t('bp_rit3_tip'),
      color: 'from-[#050010] to-[#090018]',
      border: 'border-violet-500/20',
      accent: 'text-violet-400',
      accentBg: 'bg-violet-600/15 border-violet-500/20',
    },
    {
      id: 4,
      icon: 'ri-heart-pulse-line',
      title: t('bp_rit4_title'),
      subtitle: t('bp_rit4_sub'),
      duration: t('bp_rit4_duration'),
      difficulty: t('bp_rit4_level'),
      products: ['Blue Protect Powder', 'Blue Protect Spray'],
      steps: [
        { num: 1, text: t('bp_rit4_s1') },
        { num: 2, text: t('bp_rit4_s2') },
        { num: 3, text: t('bp_rit4_s3') },
        { num: 4, text: t('bp_rit4_s4') },
      ],
      tip: t('bp_rit4_tip'),
      color: 'from-[#070015] to-[#0a0820]',
      border: 'border-fuchsia-500/15',
      accent: 'text-fuchsia-400',
      accentBg: 'bg-fuchsia-600/15 border-fuchsia-500/20',
    },
  ];

  const r = rituals[activeRitual];

  return (
    <section id="rituelen" className="w-full py-20 bg-[#060012]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">

        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">{t('bp_rit_badge')}</p>
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
            {t('bp_rit_title').slice(0, -6)}
            <span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">
              {t('bp_rit_title').slice(-6)}
            </span>
          </h2>
          <p className="text-white/45 text-sm max-w-xl mx-auto leading-relaxed">
            {t('bp_rit_sub')}
          </p>
        </div>

        {/* Ritual selector */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {rituals.map((ritual, i) => (
            <button
              key={ritual.id}
              onClick={() => setActiveRitual(i)}
              className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                activeRitual === i
                  ? `bg-gradient-to-br ${ritual.color} ${ritual.border} border`
                  : 'bg-[#0a0820]/40 border-violet-500/10 hover:border-violet-500/25'
              }`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg mb-3 ${activeRitual === i ? ritual.accentBg : 'bg-[#0a0820] border border-violet-500/10'}`}>
                <i className={`${ritual.icon} text-sm ${activeRitual === i ? ritual.accent : 'text-white/30'}`} />
              </div>
              <p className={`font-bold text-xs leading-tight mb-1 ${activeRitual === i ? 'text-white' : 'text-white/45'}`}>{ritual.title}</p>
              <p className={`text-xs ${activeRitual === i ? ritual.accent : 'text-white/25'}`}>{ritual.duration}</p>
            </button>
          ))}
        </div>

        {/* Active Ritual Detail */}
        <div className={`bg-gradient-to-br ${r.color} border ${r.border} rounded-3xl p-6 lg:p-10`}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Left info */}
            <div className="lg:col-span-1">
              <div className={`w-12 h-12 flex items-center justify-center rounded-2xl ${r.accentBg} mb-5`}>
                <i className={`${r.icon} ${r.accent} text-2xl`} />
              </div>
              <h3 className="text-white font-black text-2xl mb-1">{r.title}</h3>
              <p className="text-white/40 text-sm mb-6">{r.subtitle}</p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-timer-line text-white/30 text-sm" />
                  </div>
                  <div>
                    <p className="text-white/30 text-xs">{t('bp_rit_duration')}</p>
                    <p className="text-white text-sm font-semibold">{r.duration}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-bar-chart-line text-white/30 text-sm" />
                  </div>
                  <div>
                    <p className="text-white/30 text-xs">{t('bp_rit_level')}</p>
                    <p className="text-white text-sm font-semibold">{r.difficulty}</p>
                  </div>
                </div>
              </div>

              <p className="text-white/30 text-xs font-semibold uppercase tracking-wider mb-3">{t('bp_rit_products')}</p>
              <div className="space-y-2 mb-5">
                {r.products.map(prod => (
                  <div key={prod} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${r.accentBg}`}>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-checkbox-circle-fill ${r.accent} text-sm`} />
                    </div>
                    <span className="text-white/70 text-xs">{prod}</span>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-black/30 border border-white/5 rounded-xl">
                <p className={`${r.accent} text-xs font-semibold mb-1 flex items-center gap-1`}>
                  <i className="ri-lightbulb-line" /> {t('bp_rit_tip')}
                </p>
                <p className="text-white/50 text-xs leading-relaxed">{r.tip}</p>
              </div>
            </div>

            {/* Right steps */}
            <div className="lg:col-span-2">
              <p className="text-white/30 text-xs font-semibold uppercase tracking-wider mb-5">{t('bp_rit_steps')}</p>
              <div className="space-y-4">
                {r.steps.map((step, i) => (
                  <div key={step.num} className="flex gap-4">
                    <div className="shrink-0">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full ${r.accentBg} border`}>
                        <span className={`${r.accent} font-black text-sm`}>{step.num}</span>
                      </div>
                      {i < r.steps.length - 1 && (
                        <div className="w-px h-full ml-4 mt-1 bg-gradient-to-b from-violet-500/20 to-transparent min-h-[20px]" />
                      )}
                    </div>
                    <div className="pb-4">
                      <p className="text-white/65 text-sm leading-relaxed">{step.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};
